import { useQuery } from '@tanstack/react-query';
import { getBookCoverToken } from '../api/books';

export const bookCoverTokenKeys = {
  detail: (bookId: number) => ['book-cover-token', bookId] as const,
};

// Resolves an absolute, signed cover URL for a book, only once `enabled` (the
// card is in view / has a cover to show) — <img> can't send an Authorization
// header, so the backend mints a short-lived ?t= token instead
// (GET /api/books/:id/cover-token). react-query's default staleTime (10min)
// keeps this well under the token's 1hr expiry, so a stale cache entry is
// always still valid.
//
// The token is scoped to the book id, not a specific route, so the same
// token authorizes both /cover and /cover-thumb (see
// speed-up-frontend-page-loads design.md Decision 3) — `variant` picks which
// path this call resolves to.
export function useBookCoverUrl(
  bookId: number,
  enabled: boolean,
  variant: 'full' | 'thumb' = 'full'
): string | null {
  const { data } = useQuery({
    queryKey: bookCoverTokenKeys.detail(bookId),
    queryFn: () => getBookCoverToken(bookId),
    enabled,
  });

  if (!data?.token) return null;
  const path = variant === 'thumb' ? 'cover-thumb' : 'cover';
  return `/api/books/${bookId}/${path}?t=${data.token}`;
}
